import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import BundleWizardStepper from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import RegionCloneStep from "@/features/regions/ui/region-clone-step";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { useCloneRegion } from "@/features/regions/hooks/use-clone-region";

const STEPS = [
  { key: "target", label: "Target" },
  { key: "products", label: "Products & prices" },
  { key: "confirm", label: "Confirm" },
];

export default function RegionCloneWizard() {
  const navigate = useNavigate();
  const { data: regions } = useActiveRegions();
  const clone = useCloneRegion();

  const [current, setCurrent] = useState(0);
  const [targetId, setTargetId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>(
    {},
  );
  const [productIds, setProductIds] = useState<string[]>([]);
  const [toCloneCount, setToCloneCount] = useState(0);

  const target = regions?.find((r) => r.id === targetId);
  const result = clone.data;

  const submit = () =>
    clone.mutate(
      {
        sourceRegionId: sourceId,
        targetRegionId: targetId,
        priceOverrides,
        productIds,
      },
      {
        onError: (e: any) =>
          toast({
            title: "Error",
            description: e.response?.data?.message || "Failed to clone region",
            variant: "destructive",
          }),
      },
    );

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("..")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">Clone region</h1>
        </div>

        <BundleWizardStepper steps={STEPS} current={current} />

        <Card>
          <CardContent className="space-y-5 pt-6">
            {current === 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Clone into</p>
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Target region" />
                  </SelectTrigger>
                  <SelectContent>
                    {(regions ?? []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {codeToFlag(r.code)} {r.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {current === 1 && target && (
              <RegionCloneStep
                targetRegionId={targetId}
                sourceId={sourceId}
                onSourceChange={setSourceId}
                onPricesChange={setPriceOverrides}
                onSelectionChange={setProductIds}
                onMeta={({ toCloneCount }) => setToCloneCount(toCloneCount)}
              />
            )}

            {current === 2 && (
              <div className="space-y-3">
                {result ? (
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="h-4 w-4" /> Clone complete.
                    </p>
                    <ul className="text-sm text-gray-600 list-disc pl-5">
                      <li>Bundles created: {result.bundlesCreated}</li>
                      <li>Payment pages created: {result.paymentPagesCreated}</li>
                      <li>Configs created: {result.configsCreated}</li>
                      <li>
                        Products skipped (already present):{" "}
                        {result.productsSkipped}
                      </li>
                    </ul>
                    <p className="text-xs text-amber-600">
                      Next: set up this region's teams / managed events / event
                      types (sessions) and link its onboarding forms.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Clone {toCloneCount} offering(s) into {target?.display_name}?
                    Existing products are skipped.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                onClick={() =>
                  current === 0 ? navigate("..") : setCurrent((c) => c - 1)
                }
                disabled={clone.isPending}
              >
                {current === 0 ? "Cancel" : "Back"}
              </Button>

              {current < 2 ? (
                <Button
                  onClick={() => setCurrent((c) => c + 1)}
                  disabled={
                    (current === 0 && !targetId) ||
                    (current === 1 && (!sourceId || toCloneCount === 0))
                  }
                >
                  Next
                </Button>
              ) : result ? (
                <Button onClick={() => navigate("..")}>Done</Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={clone.isPending || toCloneCount === 0}
                >
                  {clone.isPending ? "Cloning…" : "Clone region"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </HasAccess>
  );
}
